import { ReactNode } from 'react';
import { Box, Center, Heading, Link, Stack, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import Lottie from 'lottie-react';
import Span from '@inspire/ui/chakra/Span';
import { roundButtonProps } from '@inspire/ui/chakra/theme';
import Layout from 'components/layout/Layout';
import GenericLandingPage from 'components/landing-pages/generic-landing-page';
import LinkButton from 'components/general/LinkButton';
import { OnlyLoggedOut } from 'components/auth/Redirects';
import { ATELIERS_PATH } from 'lib/paths';
import {
  GTM_ACTION_LANDING_PAGE_PROF,
  GTM_CATEGORY_LANDING_PAGES,
  pushEvent,
} from 'lib/gtm';

import maletteAnimation from '../../public/img/animations/animation-malette.lottie.json';
import { inspireYellow } from '@inspire/ui/static';
import { cdnURL } from '@inspire/util/cdn';

const LinkWithYellowHover = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <NextLink href={href}>
    <Link _hover={{ bgColor: 'yellow.200', textDecoration: 'none' }}>
      {children}
    </Link>
  </NextLink>
);

const pageConfig = {
  hero: {
    title: 'Accompagner vos élèves dans leur orientation',
    subtitle: 'Des outils clés en main',
    Animation: () => (
      <Box
        my={5}
        mx="auto"
        width={{ sm: '90%', md: '80%' }}
        lineHeight={0}
        maxW={['300px', null, '390px']}
      >
        <Lottie animationData={maletteAnimation} loop={true} />
      </Box>
    ),
    cta: 'Accompagner mes élèves',
    onCTAClick: () =>
      pushEvent(
        GTM_CATEGORY_LANDING_PAGES,
        GTM_ACTION_LANDING_PAGE_PROF,
        'Accompagner mes élèves'
      ),
    text: (
      <>
        <Text>
          Nous savons que l’orientation est un défi qui n’appartient pas qu’aux
          élèves.
        </Text>
        <Text>
          La plateforme INSPIRE vous propose des solutions pour renforcer
          l’accompagnement de vos élèves <b>sur l’orientation post-bac</b>. Aide
          à la réflexion, informations sur les filières, admissions, vie
          étudiante, codes du monde professionnel... nos modules pédagogiques à
          faire en classe vous permettent d’outiller vos élèves sur ces sujets.
        </Text>
        <Text>
          Pour aider vos élèves à se projeter dans la vie après le bac, vous
          pourrez vous appuyer sur des témoignages et notre communauté
          d’étudiants bénévoles : <b>les Éclaireurs</b>.
        </Text>
      </>
    ),
  },
  profil: 'prof',
  videoCommentCaMarcheURL: cdnURL('/INSPIRE_SYLVAIN_VERTICAL_V3.mp4'),
  videoCommentCaMarcheMiniatureUrl:
    '/img/landing-pages/Miniature_video_SYLVAIN_PROF.png',
  timelineContent: {
    heading: 'Avec INSPIRE, accompagnez vos élèves en 3 étapes',
    items: [
      {
        title: 'Accédez gratuitement aux modules',
        text: (
          <>
            Découvrez nos activités et ressources sur l’orientation post-bac, à
            utiliser directement en classe ou à distance avec vos élèves.
          </>
        ),
        imgURL: '/img/landing-pages/book.png',
      },
      {
        title: "Préparez vos séances d'accompagnement à l’orientation",
        text: (
          <>
            <Box mb={2}>
              Profitez de notre kit de survis mis à votre disposition
              gratuitement pour animer vos séances dédiées à l’orientation (des
              ressources sur la vie étudiante, les aides à l’emploi...).
            </Box>
          </>
        ),
        imgURL: '/img/landing-pages/textbox.png',
      },
      {
        title: 'Faites découvrir la plateforme à vos élèves',
        text: 'Invitez-les simplement à explorer les modules INSPIRE depuis leur espace lycéen, pour réaliser les activités pédagogiques avec vous ou en autonomie.',
        imgURL: '/img/landing-pages/magnifier.png',
      },
    ],
  },
  subVideoContent: (
    <Stack>
      <Heading textAlign="center" mt={0} fontWeight="bold" fontSize="2xl">
        Des modules pour parler d’orientation avec toutes vos classes
      </Heading>

      <Center>
        <LinkButton
          href="/modules"
          colorScheme="black"
          variant="outline"
          {...roundButtonProps}
        >
          En savoir plus sur les modules pédagogiques
        </LinkButton>
      </Center>
    </Stack>
  ),
  timelineSubCTA: (
    <>
      <Span bgColor={inspireYellow}>INSPIRE intervient également en lycée</Span>
      . Nos équipes animent différents ateliers dans les établissements ciblés
      comme prioritaires en partenariat avec les rectorats (QPV, zones
      rurales,...). <br />
      <LinkWithYellowHover href={ATELIERS_PATH}>
        En savoir plus sur le lieu de nos actions
      </LinkWithYellowHover>
      .
    </>
  ),
  roles: [
    {
      role: 'Étudiant',
      text: 'Tu es étudiant dans le supérieur et tu souhaites partager ton expérience avec des lycéens sur ta formation et la vie étudiante ? ',
      imgURL: 'https://source.unsplash.com/random/200/?education',
      button: 'Étudiant Éclaireur',
      href: 'https://inspire-orientation.org/etudiant',
    },
    {
      role: 'Lycéen',
      text: 'Tu es lycéen et tu es un peu perdu quand on te demande ce que tu veux faire plus tard ? Tu as besoin de plus d’informations sur des domaines d’études que tu envisages ?',
      imgURL: 'https://source.unsplash.com/random/200/?education',
      button: 'Lycéen',
      href: 'https://inspire-orientation.org/lyceen',
    },
  ],
};

const IndexPage = () => (
  <>
    <GenericLandingPage pageConfig={pageConfig} />
  </>
);

IndexPage.getWrappers = (page) => (
  <OnlyLoggedOut>
    <Layout variant="simple" withMainRow={false}>
      {page}
    </Layout>
  </OnlyLoggedOut>
);

export default IndexPage;
